"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { X } from "lucide-react";

/**
 * Pop-up poster for current offers.
 * - Shows once per browser session (uses sessionStorage)
 * - Separate images for desktop and mobile
 * - Closes on X click, backdrop click, or Escape key
 */
export default function OfferPopup() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const alreadyShown = sessionStorage.getItem("skinsio-offer-popup-shown");
    if (!alreadyShown) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        sessionStorage.setItem("skinsio-offer-popup-shown", "true");
      }, 1500); // 1.5s delay after page load
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={() => setIsOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Current offer"
    >
      <div
        className="relative w-full max-w-md sm:max-w-2xl lg:max-w-3xl animate-in fade-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={() => setIsOpen(false)}
          aria-label="Close offer popup"
          className="absolute -top-3 -right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-800 shadow-lg hover:bg-gray-100 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Clickable poster -> change href to your booking page or offer page */}
        <Link href="/#booking" onClick={() => setIsOpen(false)}>
          {/* Desktop image */}
          <div className="hidden sm:block relative w-full max-h-[85vh] rounded-2xl overflow-hidden shadow-2xl bg-white">
            <Image
              src="/offers/current-offer-desktop.webp"
              alt="Current offer at SkinSio Clinics"
              width={1200}
              height={700}
              className="w-full h-auto max-h-[85vh] object-contain"
              priority
            />
          </div>

          {/* Mobile image */}
          <div className="block sm:hidden relative w-full max-h-[80vh] rounded-2xl overflow-hidden shadow-2xl bg-white">
            <Image
              src="/offers/current-offer-mobile.webp"
              alt="Current offer at SkinSio Clinics"
              width={800}
              height={1000}
              className="w-full h-auto max-h-[80vh] object-contain"
              priority
            />
          </div>
        </Link>
      </div>
    </div>
  );
}
